<svelte:options runes={false} />
<script>
  // Candlestick chart with signal markers (U12b/KTD11). Hand-rolled Canvas 2D, matching the repo's zero-chart-
  // dependency idiom + the shared chartUtils helpers. Renders the transparency series: a candle per point (where
  // the OHLCV store had a candle), the strategy's signal as a marker (▲ buy / ▼ sell / · hold) at each candle,
  // and a HONEST gap indicator where a captured signal row has no candle (KTD11 — the marker still renders, the
  // candle is shown as a hollow gap tick rather than silently dropped).
  import { onMount } from 'svelte';
  import { COLORS, PADDING, clearCanvas, drawTimeLabels, indexFromX } from './chartUtils.js';

  // SignalSeriesPoint[] ascending by time: { time, open?, high?, low?, close?, signal, decision, indicators }.
  export let data = [];

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 320;
  let hoverIndex = -1;

  $: points = Array.isArray(data) ? data : [];
  $: hasData = points.length >= 1;

  // Price scale spans the candle highs/lows where present; falls back to close where a candle is gapped.
  function priceBounds(pts) {
    let lo = Infinity, hi = -Infinity;
    for (const p of pts) {
      const h = p.high ?? p.close, l = p.low ?? p.close;
      if (h != null && h > hi) hi = Number(h);
      if (l != null && l < lo) lo = Number(l);
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 1; }
    if (lo === hi) { lo -= 1; hi += 1; }
    const pad = (hi - lo) * 0.08;
    return { lo: lo - pad, hi: hi + pad };
  }

  function resize() {
    if (!container) return;
    width = Math.max(240, container.offsetWidth);
    height = 320;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);

    const { lo, hi } = priceBounds(points);
    const plotW = width - PADDING.left - PADDING.right;
    const plotH = height - PADDING.top - PADDING.bottom;
    const n = points.length;
    const xOf = (i) => PADDING.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const yOf = (v) => PADDING.top + (1 - (v - lo) / (hi - lo)) * plotH;

    // Gridlines + price labels.
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillStyle = COLORS.label;
    for (let i = 0; i <= 4; i++) {
      const v = hi - ((hi - lo) * i) / 4;
      const y = yOf(v);
      ctx.beginPath(); ctx.moveTo(PADDING.left, y); ctx.lineTo(width - PADDING.right, y); ctx.stroke();
      ctx.fillText(v.toFixed(2), width - 5, y + 3);
    }
    drawTimeLabels(ctx, points, height, { xOf });

    // Candle body width (clamped) — leave a gap between candles.
    const cw = Math.max(2, Math.min(14, plotW / Math.max(1, n) * 0.6));

    points.forEach((p, i) => {
      const x = xOf(i);
      const hasCandle = p.open != null && p.high != null && p.low != null && p.close != null;
      if (hasCandle) {
        const up = Number(p.close) >= Number(p.open);
        const color = up ? COLORS.gain : COLORS.loss;
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        // Wick.
        ctx.beginPath();
        ctx.moveTo(x, yOf(Number(p.high)));
        ctx.lineTo(x, yOf(Number(p.low)));
        ctx.stroke();
        // Body.
        const yO = yOf(Number(p.open)), yC = yOf(Number(p.close));
        const top = Math.min(yO, yC);
        const bh = Math.max(1, Math.abs(yC - yO));
        ctx.fillRect(x - cw / 2, top, cw, bh);
      } else {
        // Gap: the OHLCV store lacked a candle for this captured signal row (KTD11). Show a hollow tick at the
        // close (if any) so the signal marker has an anchor, never silently dropped.
        const y = p.close != null ? yOf(Number(p.close)) : (PADDING.top + plotH / 2);
        ctx.strokeStyle = COLORS.label;
        ctx.globalAlpha = 0.5;
        ctx.strokeRect(x - cw / 2, y - 2, cw, 4);
        ctx.globalAlpha = 1;
      }

      // Signal marker: ▲ buy (signal 1) / ▼ sell (signal -1) / · hold (0). Anchored near the candle low/high.
      const sig = Number(p.signal ?? 0);
      if (sig !== 0) {
        const anchor = p.low != null ? yOf(Number(p.low)) + 10 : (p.close != null ? yOf(Number(p.close)) + 10 : PADDING.top + plotH / 2);
        ctx.fillStyle = sig > 0 ? COLORS.gain : COLORS.loss;
        ctx.beginPath();
        if (sig > 0) { ctx.moveTo(x, anchor - 6); ctx.lineTo(x - 4, anchor); ctx.lineTo(x + 4, anchor); }
        else { ctx.moveTo(x, anchor + 6); ctx.lineTo(x - 4, anchor); ctx.lineTo(x + 4, anchor); }
        ctx.closePath();
        ctx.fill();
      }
    });

    // Crosshair.
    if (hoverIndex >= 0 && hoverIndex < n) {
      const x = xOf(hoverIndex);
      ctx.strokeStyle = COLORS.crosshair;
      ctx.globalAlpha = 0.4;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(x, PADDING.top); ctx.lineTo(x, height - PADDING.bottom); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
  }

  $: if (ctx && canvas && hasData && width && height && points && (hoverIndex || hoverIndex === -1)) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverIndex = indexFromX(e.clientX - rect.left, points.length, width);
  }
  function onLeave() { hoverIndex = -1; }

  $: hovered = hoverIndex >= 0 && hoverIndex < points.length ? points[hoverIndex] : null;

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Candles &amp; Signals</h4>
  {#if hasData}
    <canvas bind:this={canvas} width={width} height={height} tabindex="0"
      aria-label={`Candle chart with ${points.length} points and trade-signal markers`}
      on:mousemove={onMove} on:mouseleave={onLeave}></canvas>
    {#if hovered}
      <div class="at-candle-hover" aria-live="polite">
        {new Date(hovered.time).toLocaleString()} ·
        {#if hovered.close != null}O {Number(hovered.open).toFixed(2)} H {Number(hovered.high).toFixed(2)} L {Number(hovered.low).toFixed(2)} C {Number(hovered.close).toFixed(2)}{:else}<em>no candle (signal only)</em>{/if}
        · signal {hovered.signal}
      </div>
    {/if}
  {:else}
    <div class="empty-state"><p class="empty-state-description">No signal data for this symbol/window yet.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
  .at-candle-hover { font-size: 0.75rem; color: var(--text-muted, #8a8b8d); padding: 0.25rem 0; font-variant-numeric: tabular-nums; }
</style>
